// client/src/components/auth/AuthLoadError.tsx
// 868mbzt38 D5 — full-page auth load error.
// Auth query failed AND there is no cached user; we can't pretend they're
// anonymous (Landing/Login would suggest re-login when the session cookie
// is still alive), and we can't route them into the portal (no data).
// One translated message (errors.http.serviceUnavailable, or
// errors.http.tooManyRequests for a 429), no raw `error.message` in the
// body — that may be English and contain implementation detail we'd rather
// not ship to the user.

import { FormattedMessage } from 'react-intl';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RateLimitedError } from '@/lib/queryClient';

interface AuthLoadErrorProps {
  // The contract binds this component to a non-null error. We accept it
  // for symmetry with React's error-boundary pattern (and to keep the
  // debug surface in case the cause needs to be visible during an
  // incident: the call-site logs it). The body only reads its type, so the
  // raw `error.message` never reaches the screen.
  error: Error;
  onRetry: () => void;
}

export function AuthLoadError({ error, onRetry }: AuthLoadErrorProps) {
  // N11 (868mbzt38): a 429 is not an outage — the user has to wait.
  const messageId =
    error instanceof RateLimitedError ? 'errors.http.tooManyRequests' : 'errors.http.serviceUnavailable';

  return (
    <div
      data-testid="auth-load-error"
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 py-16"
    >
      <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
      <p className="max-w-md text-center text-base text-foreground">
        <FormattedMessage id={messageId} />
      </p>
      <Button variant="default" onClick={onRetry} data-testid="button-auth-retry">
        <FormattedMessage id="common.actions.retry" />
      </Button>
    </div>
  );
}
